import React from "react";
import { CheckCircle2 } from "lucide-react";

// 6 clinical pain points + their human framing. Each card pairs the name
// with a luxury 3D medical render (cream + navy palette) generated to
// match the page aesthetic — replaces the prior inline SVG glyphs.
const PAIN_POINTS = [
  {
    name: "Neck Pain",
    body: "Your old pillow forces your cervical spine into a sharp curve all night. The Dubai Pillow contours to keep it neutrally aligned — the same alignment chiropractors aim for in treatment.",
    image: "/assets/clinical/clinical_neck.png",
    key: "neck",
  },
  {
    name: "Back Pain",
    body: "Misaligned head = misaligned spine. Down to the lumbar. Our adjustable loft system supports natural spinal curvature so the rest of your back can finally rest.",
    image: "/assets/clinical/clinical_back.png",
    key: "back",
  },
  {
    name: "Morning Migraines",
    body: "Tension headaches often start in the suboccipital muscles overnight. By removing pressure points behind the skull, the Dubai Pillow lets blood and lymph flow freely until morning.",
    image: "/assets/clinical/clinical_migraine.png",
    key: "migraine",
  },
  {
    name: "Shoulder Pains",
    body: "If you sleep on your side, your shoulder bears the weight of your entire torso. Our pinch-tuck chambers cradle the shoulder joint without compression — no more pinched nerves at 3 a.m.",
    image: "/assets/clinical/clinical_shoulder.png",
    key: "shoulder",
  },
  {
    name: "Numbness & Tingling",
    body: "That pins-and-needles feeling in your arms? It's a flattened pillow cutting circulation. Our 800+ fill-power down keeps loft for 5+ years so blood keeps flowing all night, every night.",
    image: "/assets/clinical/clinical_hand.png",
    key: "hand",
  },
  {
    name: "Muscle Tension",
    body: "Bad sleep tightens the trapezius, levator scapulae and surrounding muscles. Wake up with cement shoulders no more — let the down release them while you sleep.",
    image: "/assets/clinical/clinical_muscle.png",
    key: "muscle",
  },
];

const CLAIMS = [
  "Recommended by chiropractors and sleep specialists worldwide",
  "Helps you return to an active, pain-free, energized life",
  "Avoids the cost and risk of unnecessary back / neck surgery",
  "Saves you years of money lost on inferior pillows you keep replacing",
  "Right now: the lowest price-per-pillow we've ever offered — $74/ea on bundles",
];

export default function ClinicalRelief() {
  return (
    <section
      data-testid="clinical-relief"
      className="bg-[#f5f7fb] py-20 md:py-28"
      id="clinical"
    >
      <div className="container-x">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-16 items-start">
          {/* LEFT: 6 pain-point anatomy cards */}
          <div>
            <span className="section-pill">🩻 What your old pillow is doing to you</span>
            <h2 className="mt-5 text-[30px] sm:text-[40px] md:text-[48px] leading-[1.04] font-black tracking-[-0.02em] text-[#0e0e10]">
              Stop the damage
              <br />
              your old pillow is causing.
            </h2>

            <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {PAIN_POINTS.map((p) => (
                <div
                  key={p.name}
                  className="flex flex-col items-center text-center"
                  data-testid={`pain-${p.key}`}
                >
                  <div
                    className="w-full aspect-square max-w-[140px] rounded-full overflow-hidden ring-1 ring-[#1e3a8a]/10"
                    style={{ boxShadow: "0 10px 30px -12px rgba(30, 58, 138, 0.25)" }}
                  >
                    <img
                      src={p.image}
                      alt={`${p.name} — anatomical illustration`}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="mt-3 font-extrabold text-[13px] text-[#0e0e10] leading-tight">
                    {p.name}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* RIGHT: claims / persuasion column */}
          <div className="lg:sticky lg:top-24">
            <div className="text-[11px] uppercase tracking-[0.22em] font-extrabold text-[#1e3a8a]">
              Professionally Recommended
            </div>
            <h3 className="mt-3 text-[26px] sm:text-[32px] md:text-[36px] leading-[1.1] font-black tracking-[-0.02em] text-[#0e0e10]">
              The pillow specialists recommend to <span className="text-[#1e3a8a]">STOP</span> the damage.
            </h3>

            <p className="mt-5 text-[15px] text-[#3a3a40] leading-relaxed">
              After 15 months of prototyping with sleep physiotherapists in Dubai and Vienna, the
              Dubai Pillow was released to a small waitlist — and the verdict has been universal.
            </p>
            <p className="mt-3 text-[15px] text-[#3a3a40] leading-relaxed">
              It positions your body so naturally that no nerve centre is compressed,
              your cervical spine and head sit perfectly aligned, and the deep, restful sleep
              you remember from childhood becomes possible again.
            </p>

            <ul className="mt-7 space-y-3.5">
              {CLAIMS.map((c) => (
                <li key={c} className="flex items-start gap-3 text-[14.5px] text-[#0e0e10]">
                  <span className="shrink-0 w-5 h-5 rounded-full bg-[#1e3a8a] inline-flex items-center justify-center mt-0.5">
                    <CheckCircle2 size={11} className="text-white" strokeWidth={3} />
                  </span>
                  <span className="leading-snug">{c}</span>
                </li>
              ))}
            </ul>

            <a
              href="#shop"
              data-testid="clinical-relief-cta"
              className="pill-btn inline-flex items-center gap-2 mt-8 px-7 py-3.5 text-[13px]"
            >
              See the pillow that fixes this →
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
